import React from 'react';
import { X, Info, FileStack, Loader2 } from 'lucide-react';
// pantallas-alumno (G), PE11 y PE22-d de la parte 2: la ventana del alumno (el velo de la casa y la caja de la
// ventana H del Panel), el boton que trabaja con giro y el area de 44 de la X.
import { FONDO_DE_LA_VENTANA } from './panel/VentanaDelPanel';
import { useHojaDeLaCasa, FilaDeAgarradera, ENTRA_EL_VELO, ENTRA_LA_CAJA } from './alumno/arrastreDeHoja';
import { AreaDe44 } from './panel/anatomia';
import { GiroEncima, APRETADO } from './alumno';
import { useReadyTranslation } from '../hooks/useReadyTranslation';
import { trackEvent } from '../lib/analytics';
import { classifyWall } from './autoSplitWall';
import AutoSplitSuggestionCards from './AutoSplitSuggestionCards';
import PartsMap, { buildModalSegments, ColoresDeLaBarra } from './PartsMap';
import { tituloDeLaVentana, renglonDeLasDemas, renglonDelDesborde, accionDeOferta, ofertasDeSeries7, conDesborde, leyendaDeSeries7, nombresDeLosTramos, fechaDelReinicio } from '../lib/series7';

/**
 * AutoSplitModal — Phase 2: render-only view of the server's `affordability` payload.
 *
 * The backend (preview / GET temp-upload) computes EVERYTHING: how many parts the user
 * can process now (k of n), what binds them, whether the rest fits the pending tope
 * (pending_fit), and 0-2 fully-resolved upsell suggestion cards. This component does
 * ZERO business math — it only paints affordability and routes clicks to callbacks.
 *
 * Screens (by `wall`):
 *   full     k == n                      → process all
 *   partial  0 < k < n  && pending_fit   → process k now, save the rest
 *   k0       k == 0     && pending_fit   → save all to pending
 *   overcap  !pending_fit                → suggestion cards + free-space (pending tope full)
 *   contact  !pending_fit && contact_support     → top plan maxed → free up pending
 *   impossible !pending_fit && impossible_in_any_plan → manual-split tip
 */

/* ── REMATE APP-1, R3 — ESTOS DOS BOTONES VIVÍAN DENTRO DEL CUERPO DE RENDER ──────
   Y ésa era la causa del «pulsé "Procesar las 2" y no pasó nada» que Carlos vio en
   producción el 11-sep. Declarar un componente dentro del render crea una FUNCIÓN
   nueva en cada pasada, o sea un TIPO nuevo; React compara `element.type` por
   identidad, no reconcilia, y en vez de actualizar el botón BORRA su fiber y monta
   otro: el nodo del DOM se destruye y se vuelve a crear entero, con los mismos bytes.

   Debajo de esta ventana el Panel repinta dos veces por segundo: `Dashboard.js:994`
   filtra `docs.filter(d => d.status === 'processing')` y `Dashboard.js:1000` enciende
   `setInterval(() => setNowTick(Date.now()), 500)` con eso — y el placeholder que abre
   esta ventana nace justamente con `status: "processing"` (`documents.py:660`). El
   documento mantenía vivo el reloj que le rompía su propio botón.

   En el navegador `click` se dispara sobre el ancestro común de los blancos de
   `mousedown` y `mouseup`: si al nodo del `mousedown` lo remontaron a media pulsación,
   el `onClick` del botón no está en ese camino y NO CORRE. Ni petición, ni aviso, ni
   cierre. Y no es un estado que se arregle: es una CARRERA, por eso «a la segunda sí».

   La X (`:229-236`) y el enlace de descarte del pie son `<button>` planos, nunca se
   remontaron, y por eso sí respondieron a la primera. Ésa es la firma del diagnóstico.

   Al subirlos aquí el tipo es estable y el nodo sobrevive a los repintados. `disabled`
   pasa a ser prop porque `confirmLoading` ya no está en el alcance: los siete sitios lo
   pasan, así que la conducta no cambia en ninguno.

   pantallas-alumno (G), PE11 y PE22-d de la parte 2: los botones de la casa. El principal es el naranja a lo ancho
   de 48 y, mientras la ventana manda (`disabled`, que en todos los sitios es `confirmLoading`), TRABAJA: el texto
   deja de verse pero conserva su lugar y el giro va encima (mismo ancho). El secundario es el del Panel. */
const BOTON_DE_LA_VENTANA = 'relative w-full h-12 px-4 rounded-xl text-[14px] font-semibold inline-flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-white';
function PrimaryBtn({ label, onClick, testid, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-busy={disabled || undefined}
      data-trabajando={disabled ? 'true' : undefined}
      className={`${BOTON_DE_LA_VENTANA} bg-[#F27A2E] text-[#160A02] disabled:cursor-not-allowed ${APRETADO}`}
      data-testid={testid}
    >
      <GiroEncima trabajando={!!disabled}>{label}</GiroEncima>
    </button>
  );
}

function SecondaryBtn({ label, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className={`${BOTON_DE_LA_VENTANA} bg-[#1B1B1B] border border-[#2C2C2C] text-[#F4F4F3] ${APRETADO}`}
      data-testid={testid}
    >
      {label}
    </button>
  );
}

/* pantallas-alumno (G): LA CAJA DE LA VENTANA DEL ALUMNO, 480. El velo de la casa (`FONDO_DE_LA_VENTANA`: fijo en
   los cuatro bordes, negro al 66 %, z 70, sobre la barra de arriba y la de abajo); la caja de la ventana H del Panel
   (#161616, borde #2A2A2A, radio 20; en el celular hoja desde abajo con agarradera y radio 24 arriba); el cuerpo se
   desplaza por dentro y el pie (los botones) queda fijo abajo, con la zona segura de abajo en el celular. La X es la
   de hoy (mismo testid, misma etiqueta), con su area de 44. */
const CAJA_DE_LA_VENTANA = 'relative w-full md:max-w-[480px] max-h-[90dvh] flex flex-col bg-[#161616] border border-[#2A2A2A] rounded-t-[24px] md:rounded-[20px] shadow-2xl';
const CUERPO_DE_LA_VENTANA = 'flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 pt-4 md:pt-6';
const PIE_DE_LA_VENTANA = 'shrink-0 max-h-[55dvh] overflow-y-auto px-6 pt-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] md:pb-6';

// marca-1 (#726): la agarradera de la casa, con su arrastre: bajarla 80 o un tiron cierra como la X.
function Agarradera({ agarradera }) {
  return <FilaDeAgarradera agarradera={agarradera} testid="auto-split-agarradera" />;
}

function XDeLaVentana({ onClose, etiqueta }) {
  return (
    <button
      onClick={() => onClose && onClose()}
      className="modal-close-tap absolute top-2.5 right-3 md:top-4 md:right-4 z-[1] w-10 h-10 rounded-xl flex items-center justify-center text-[#9A9A9A] hoverable:text-white hoverable:bg-[#1B1B1B] focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
      data-testid="auto-split-close"
      aria-label={etiqueta}
    >
      <AreaDe44 />
      <X className="w-5 h-5" />
    </button>
  );
}

/* post-t7-1 (Bloque H, #598): EL DOCUMENTO ENTERO. Un escaneado que ya se leyo y no alcanzo creditos: no tiene
   partes, ni troceo, ni barra, ni descartar (se borra desde Pendientes). Tres caras, de arriba a abajo:
     . guardado en Pendientes (el aviso que el Panel abre UNA vez): titulo, "Usa N creditos. Tienes M.", el archivo,
       las dos frases y Comprar pack (principal), Mejorar plan (si no es Pro) e Ir a Pendiente de procesar;
     . Pendientes lleno: titulo, la linea, el archivo, la frase de que no hay espacio y Liberar espacio;
     . invitado: titulo, la linea, el archivo, crea tu cuenta para empezar a procesarlo, su nota y Crear cuenta.
   La X cierra (quien la monta decide que significa cerrar). */
export function VentanaDocumentoEntero({ preview, t, idioma, queuePosition, onClose, onBuyPack, onUpgrade, onGoPending,
  onGuestSignup }) {
  const filename = preview.original_filename || preview.filename || '';
  const fecha = fechaDelReinicio(preview.credits_reset_at, idioma);
  const invitado = !!preview.guest_signup_required;
  const lleno = !invitado && !!preview.pendientes_lleno;
  const hoja = useHojaDeLaCasa(onClose || null);
  return (
    <div ref={hoja.veloRef} className={`${FONDO_DE_LA_VENTANA} ${ENTRA_EL_VELO}`} data-testid="auto-split-modal">
      <div ref={hoja.cajaRef} className={`${CAJA_DE_LA_VENTANA} ${ENTRA_LA_CAJA}`} role="dialog" aria-modal="true" aria-labelledby="auto-split-titulo"
        data-testid="auto-split-entero">
        <Agarradera agarradera={hoja.agarradera} />
        <XDeLaVentana onClose={onClose} etiqueta={t('dashboard:auto_split.cancel_button')} />
        <div className={CUERPO_DE_LA_VENTANA}>
        {queuePosition && queuePosition.total > 1 && (
          <div className="text-[11px] uppercase tracking-wider text-[#F7A06A] font-semibold mb-2 pr-8" data-testid="auto-split-queue-indicator">
            {t('dashboard:auto_split.queue_indicator', { current: queuePosition.current, total: queuePosition.total })}
          </div>
        )}
        <h2 id="auto-split-titulo" className="text-[17px] font-semibold text-[#F4F4F3] leading-snug pr-8" data-testid="auto-split-header">
          {t('dashboard:auto_split.entero.titulo')}
        </h2>
        <p className="text-[14px] text-[#C8C8C8] mt-2" data-testid="auto-split-entero-linea">
          {t('dashboard:auto_split.line_full', { count: preview.credits_required || 1, have: preview.credits_available || 0 })}
        </p>
        {filename && (
          <p className="text-[13px] text-[#9A9A9A] mt-1 break-words" data-testid="auto-split-entero-archivo">{filename}</p>
        )}
        <div className="mt-4 space-y-2 text-[14px] leading-5 text-[#C8C8C8]" data-testid="auto-split-entero-cuerpo">
          {invitado ? (
            <>
              <p>{t('dashboard:auto_split.guest_hook_large')}</p>
              <p className="text-[13px] text-[#9A9A9A]">{t('dashboard:auto_split.guest_waiting_note')}</p>
            </>
          ) : lleno ? (
            <p>{t('dashboard:auto_split.entero.lleno')}</p>
          ) : (
            <>
              <p>{t('dashboard:auto_split.entero.guardado')}</p>
              <p>{fecha ? t('dashboard:auto_split.entero.cuando', { date: fecha })
                : t('dashboard:auto_split.entero.cuando_sin_fecha')}</p>
            </>
          )}
        </div>
        </div>
        <div className={`${PIE_DE_LA_VENTANA} space-y-2`}>
          {invitado ? (
            <PrimaryBtn testid="auto-split-guest-cta" label={t('dashboard:auto_split.guest_cta')}
              onClick={() => onGuestSignup && onGuestSignup()} />
          ) : lleno ? (
            <PrimaryBtn testid="auto-split-free-space-pending-btn" label={t('dashboard:auto_split.btn_free_space_pending')}
              onClick={() => onGoPending && onGoPending()} />
          ) : (
            <>
              <PrimaryBtn testid="auto-split-buy-credits-btn" label={t('dashboard:auto_split.buy_pack_button')}
                onClick={() => onBuyPack && onBuyPack()} />
              {preview.plan !== 'pro' && (
                <SecondaryBtn testid="auto-split-upgrade-btn" label={t('dashboard:insufficient_credits.upgrade_btn')}
                  onClick={() => onUpgrade && onUpgrade()} />
              )}
              <SecondaryBtn testid="auto-split-go-pending-btn" label={t('dashboard:auto_split.saved_pending_link')}
                onClick={() => onGoPending && onGoPending()} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AutoSplitModal({
  isOpen,
  preview,
  mode = 'upload',  // 'upload' (Dashboard) | 'resume' (/pending section)
  onProcess,        // (processCount|undefined) -> finalize-split OR process-pending (resume)
  onSavePending,    // () -> save-pending (explicit k=0 button, upload only)
  onClose,          // () -> X / cancel (parent decides save vs warn vs cancel)
  onBuyPack,
  onUpgrade,        // (target) -> /upgrade
  onContactSupport, // () -> /support?subject=custom-plan
  onGoPending,      // (parentDocId?) -> pending section route
  onDiscard,        // () -> close WITHOUT saving (upload k0/partial only)
  onGuestSignup,    // () -> guest mode CTA: prepare-signup + persist claim + /signup
  duplicateGroup,   // pending-summary group matching duplicate_of_pending (duplicate variant)
  queuePosition,
  confirmLoading = false,
  affordabilityLoading = false,  // head advanced; numbers are being refetched → show loading, NOT stale cache
}) {
  const { t, ready, LoadingGate, i18n } = useReadyTranslation(['dashboard', 'common']);
  const [discardConfirm, setDiscardConfirm] = React.useState(false);
  // marca-1 (#726): la hoja entra y sale como las demas y cierra al bajar la agarradera (como la X). Abierta = las
  // dos caras de abajo (la del documento entero lleva su propio gancho).
  const hoja = useHojaDeLaCasa(onClose || null, {
    abierta: !!(ready && isOpen && preview && !preview.documento_entero && preview.affordability),
  });

  if (!ready) return <LoadingGate />;
  // post-t7-1 (Bloque H): un documento entero tiene su propia cara (sin partes); lo demas, byte a byte lo de antes
  if (isOpen && preview && preview.documento_entero) {
    return (
      <VentanaDocumentoEntero preview={preview} t={t} idioma={i18n && i18n.language} queuePosition={queuePosition}
        onClose={onClose} onBuyPack={onBuyPack} onUpgrade={onUpgrade} onGoPending={onGoPending}
        onGuestSignup={onGuestSignup} />
    );
  }
  if (!isOpen || !preview || !preview.affordability) return null;

  // Queue advance: the next popup's cached affordability reflects pre-consumption balances.
  // Until the fresh refetch lands, render a neutral loading body (never the old numbers), so
  // the user sees loading→correct instead of stale→correct. Brief by construction (one GET).
  if (affordabilityLoading) {
    return (
      <div ref={hoja.veloRef} className={`${FONDO_DE_LA_VENTANA} ${ENTRA_EL_VELO}`} data-testid="auto-split-modal">
        <div ref={hoja.cajaRef} className={`${CAJA_DE_LA_VENTANA} ${ENTRA_LA_CAJA}`} role="dialog" aria-modal="true" aria-label={preview.original_filename || undefined}
          data-testid="auto-split-caja">
          <Agarradera agarradera={hoja.agarradera} />
          <XDeLaVentana onClose={onClose} etiqueta={t('dashboard:auto_split.cancel_button')} />
          <div className="px-6 py-12 flex flex-col items-center justify-center gap-4" data-testid="auto-split-loading">
            <Loader2 className="w-6 h-6 text-[#F27A2E] animate-spin" />
            {preview.original_filename && (
              <p className="text-[13px] text-[#9A9A9A] truncate max-w-full">{preview.original_filename}</p>
            )}
            <div className="w-full space-y-2 pt-1">
              <div className="h-3 rounded bg-[#1f1f1f] animate-pulse w-3/4 mx-auto" />
              <div className="h-3 rounded bg-[#1f1f1f] animate-pulse w-1/2 mx-auto" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const aff = preview.affordability;
  const n = aff.n;
  const k = aff.k;
  const r = Math.max(0, n - k);
  const nextCost = preview.next_part_cost ?? 0;
  const partsAmt = (count) => t('dashboard:auto_split.parts_amount', { count });
  const snap = aff.snapshot || { credits_available: 0, slots_free: 0, pending_free: 0 };
  const isResume = mode === 'resume';

  const filename = preview.original_filename || '';
  const dup = preview.duplicate_of_pending;
  // X1: duplicate-in-pending -> simplified variant. Saving is impossible (server 409s);
  // the obvious action is to continue in the existing pending group.
  const isDuplicate = !!dup && !isResume;
  // Guest split funnel: the backend marks guest previews — affordability is the
  // hypothetical "as a free account" (B1/B5). The guest can't process or save, so the
  // only action is the signup CTA; the parts map + hook show what the account unlocks.
  const isGuestMode = !!preview.guest_signup_required && !isResume;
  // -- series-7 (bloque A, #488 #489 #492, 24-sep-2026) ------------------------------
  // `series7` es la llave aparte del sobre con lo que la ventana dice ahora: cuantas
  // partes caben HOY de cuantas, que falta (creditos, espacio o las dos), lo que no cupo
  // en Pendientes y las ofertas con su combinacion exacta. Con ella el muro nunca es
  // "demasiado grande" ni de soporte (autoSplitWall.js) y, al subir, el titulo es U2 con
  // su renglon debajo; al reanudar el encabezado queda como hoy y solo cambian las
  // tarjetas. El duplicado y el invitado NO la leen: su ventana no cambia (el invitado
  // sigue viendo la de crear cuenta, con su muro de hoy). Sin la llave, todo es lo de hoy.
  //
  // EL CARRIL DE OJOS NO DESBORDA (revision adversarial del bloque Panel, hallazgo 2,
  // 25-sep-2026): un escaneado o un paquete de fotos no puede guardar "lo que quepa" (el
  // servidor sigue con su 409 de hoy en ese carril; DETENTE del bloque de rutas), asi que
  // "Procesar k" con U6 seria un callejon sin salida. El Panel marca ese sobre con
  // `sin_desborde: true`; con la marca Y con `overflow > 0` la ventana es EXACTAMENTE la
  // de un sobre sin series7 (el muro de hoy). Sin la marca, o sin desborde, nada cambia.
  const s7SinDesborde = preview.sin_desborde === true && !!aff.series7
    && typeof aff.series7.overflow === 'number' && aff.series7.overflow > 0;
  const s7 = (aff.series7 && !isDuplicate && !isGuestMode && !s7SinDesborde) ? aff.series7 : null;
  const wall = classifyWall(s7 || !aff.series7 ? aff : { ...aff, series7: undefined }, mode);
  const idioma = i18n?.language;
  const s7Resto = s7 && !isResume ? renglonDeLasDemas(t, s7, idioma) : null;
  const s7Desborde = s7 && !isResume ? renglonDelDesborde(t, s7) : null;
  // series-7 (revision adversarial, hallazgo 1): con `saved` en cero no cabe NI UNA parte en
  // Pendientes, asi que en el muro de cero partes no hay nada que guardar. El boton de
  // guardar solo podria dar el 409 del tope (y la misma ventana volveria): no se pinta.
  const s7NadaQueGuardar = !!s7 && s7.saved === 0;
  // series-7, bloque T del remate (DETENTE 2, textos ratificados el 25-sep-2026): al SUBIR,
  // con algo que no cupo ni en Pendientes, "y guardar el resto" prometia de mas. El boton de
  // procesar usa la llave que ya existe "Procesar {{k}} ahora" y la leyenda de la barra
  // cuenta las tres (las k del boton, las guardadas y las que hay que volver a subir; con
  // cero ahora, su variante). Solo aqui: sin series7, sin desborde, con la marca
  // sin_desborde (s7 es null) o al reanudar, el boton y la leyenda son los de hoy.
  // Las k son las de `aff.k`, las mismas del boton: el servidor las calcula en la misma
  // llamada que `series7.now`, con los mismos creditos, activos y plan (iguales siempre).
  const s7ConDesborde = !!s7 && !isResume && conDesborde(s7);
  // Guest with a doc whose remainder won't fit free's Pending tope (overcap) or that no
  // plan can hold (impossible): the partial-style hook + parts bar would FALSELY promise
  // "the rest is saved in Pending for 60 days". Show an honest "large — create account to
  // see your options" hook with NO parts bar/legend. The real picture (overcap, upgrade
  // path) appears post-conversion on the free side.
  const isGuestLarge = isGuestMode && (wall === 'overcap' || wall === 'impossible');
  // ── #368 / #369 — EL CAMINO ESCANEADO ────────────────────────────────────────────
  // `documento_base_id` sólo viaja cuando el sobre sale de un placeholder que ya se leyó
  // con los ojos: el documento EXISTE, tiene su tarjeta en el Panel y está a salvo como
  // `por_confirmar` hasta los 7 días. Eso cambia dos cosas de esta ventana, y sólo dos:
  // el pie (cerrar ya no es tirar, así que el botón destructivo tiene que ser explícito)
  // y el primario del muro `k0` (ofrecer «guardar» sobre algo ya guardado es una promesa
  // vacía). El camino de texto —donde el documento todavía no existe— no se mueve.
  const esEscaneado = !!preview.documento_base_id;
  // estilos-1 (Bloque D, #780, ratificada): la oracion de las partes, el renglon de 13 px en el gris secundario de la
  // casa (#9A9A9A, COLORES.textoSecundario), al final del bloque del titulo, en toda cara que lo pinta. No va en la
  // del muro `impossible` (ahi el documento no se puede partir en ningun plan y la frase lo contradiria), ni en la de
  // cargando (sin titulo), ni en la del documento entero (sin partes). No depende de ninguna perilla.
  const porCalidad = wall === 'impossible' ? null : (
    <p className="text-[13px] text-[#9A9A9A] mt-1" data-testid="auto-split-por-calidad">
      {t('dashboard:auto_split.por_calidad')}
    </p>
  );

  const emit = (cta) => trackEvent('cta_click', { cta, wall, mode });

  const onCardClick = (c) => {
    // series-7: una oferta (lleva `kind`) abre la compra que ya existe. Pro sola o Pro mas
    // paquetes -> la de Pro (al volver la ventana se recalcula y ofrece lo que falte);
    // paquetes -> la pantalla de paquetes de hoy, que no se abre en un paquete concreto.
    if (c.kind) {
      const accion = accionDeOferta(c);
      if (accion === 'packs') { emit('buy_pack'); onBuyPack && onBuyPack(); }
      else if (accion === 'pro') { emit('upgrade_pro'); onUpgrade && onUpgrade('pro'); }
      return;
    }
    if (c.instrument === 'pack') { emit('buy_pack'); onBuyPack && onBuyPack(); }
    else if (c.instrument === 'pro') { emit('upgrade_pro'); onUpgrade && onUpgrade('pro'); }
  };

  const dCredits = aff.deficits?.credits ?? 0;
  const dSlots = aff.deficits?.slots ?? 0;
  // If a PACK suggestion card is on screen, it IS the buy CTA (with numbers) — the
  // generic "Buy credit pack" button would be a duplicate. Plan-only cards keep it.
  // series-7: con ofertas, la "tarjeta de paquete" es una oferta de paquetes solos (la de
  // Pro mas paquetes abre la compra de Pro, asi que no reemplaza al boton de comprar).
  const hasPackCard = s7
    ? ofertasDeSeries7(s7).some(o => accionDeOferta(o) === 'packs')
    : (aff.suggestions || []).some(c => c.instrument === 'pack');
  const showBuyCredits = dCredits > 0 && !hasPackCard;

  // P1/P4: ONE quiet line per limiting coin, each deficit stated WITH its goal
  // ("have X — doing Y takes Z"). Never a bare shortfall number, never 3 coins in
  // one phrase. The whole-document deficit lives only inside the suggestion cards.
  const mathLines = (() => {
    if (isDuplicate || isResume || isGuestMode) return [];   // resume merges into its subtitle; guest numbers are hypothetical
    const out = [];
    const cost = aff.total_cost ?? 0;
    if (wall === 'full') {
      out.push(t('dashboard:auto_split.line_full', { count: cost, have: snap.credits_available }));
    } else if (wall === 'partial') {
      if (dCredits > 0) out.push(t('dashboard:auto_split.line_credits', { count: snap.credits_available, n, cost }));
      if (dSlots > 0) out.push(t('dashboard:auto_split.line_spaces', { count: snap.slots_free, n }));
    } else if (wall === 'k0') {
      const firstCost = preview.chunks?.[0]?.credits_required ?? 1;
      const creditsBlock = snap.credits_available < firstCost;
      const slotsBlock = snap.slots_free < 1;
      if (creditsBlock || !slotsBlock) out.push(t('dashboard:auto_split.line_k0_credits', { count: firstCost, have: snap.credits_available }));
      if (slotsBlock) out.push(t('dashboard:auto_split.line_k0_space', { have: snap.slots_free }));
    } else if (wall === 'overcap' || wall === 'contact') {
      // P3: the Pending-storage coin only lives in over-cap rooms — with its assumption
      // (k) and what the free spaces are FOR (pending documents). O1. FIX-3: with ZERO free
      // spaces, "you only have 0 spaces free" reads oddly — use the *_nofree variants
      // ("you have no free spaces"). free >= 1 keeps the numbered copy unchanged.
      const noFree = snap.pending_free === 0;
      out.push(k > 0
        ? t(noFree ? 'dashboard:auto_split.line_overcap_k_nofree' : 'dashboard:auto_split.line_overcap_k', { count: k, r, free: snap.pending_free })
        : t(noFree ? 'dashboard:auto_split.line_overcap_k0_nofree' : 'dashboard:auto_split.line_overcap_k0', { count: n, free: snap.pending_free }));
      if (hasPackCard) out.push(t('dashboard:auto_split.line_credits', { count: snap.credits_available, n, cost }));
    }
    return out;   // impossible: no numbers (R1)
  })();

  // Visual parts map (shared with /pending): preview of what processes now vs is saved.
  const showPartsMap = (isGuestMode && !isGuestLarge) || wall === 'partial' || wall === 'k0' || (isResume && wall === 'full');
  // ajustes-1-3-0 (Bloque A2, #499): con series7 al SUBIR, la barra lleva tres tramos (las
  // k de ahora, las guardadas y las que desbordan, que hasta hoy se pintaban como
  // guardadas), debajo la lista de colores con sus nombres (T10) y la leyenda en frases
  // enteras (T5 a T9, lib/series7.js). Al reanudar, sin series7 o para el invitado, la
  // barra y la leyenda son las de hoy, byte por byte (AutoSplitModal.s7paridad).
  const s7Leyenda = !!s7 && !isResume;
  const partsSegments = showPartsMap
    ? buildModalSegments({ n, k, mode, parts: preview.parts, nextChunkIndex: preview.next_chunk_index, overflow: s7ConDesborde ? s7.overflow : 0 })
    : [];
  const partsLegend = !showPartsMap ? null
    : isResume ? t('dashboard:auto_split.parts_legend_resume', { k, count: Math.max(0, n - k) })
    : (isGuestMode && k >= n) ? null   // guest hook already says "all parts now"
    : s7Leyenda ? leyendaDeSeries7(t, s7, k)
    : (wall === 'k0' || (isGuestMode && k === 0)) ? t('dashboard:auto_split.parts_legend_k0', { parts: partsAmt(n) })
    : t('dashboard:auto_split.parts_legend', { count: k, kn: k + 1, n });

  return (
    <div ref={hoja.veloRef} className={`${FONDO_DE_LA_VENTANA} ${ENTRA_EL_VELO}`} data-testid="auto-split-modal">
      <div ref={hoja.cajaRef} className={`${CAJA_DE_LA_VENTANA} ${ENTRA_LA_CAJA}`} role="dialog" aria-modal="true" aria-labelledby="auto-split-titulo"
        data-testid="auto-split-caja">
        <Agarradera agarradera={hoja.agarradera} />
        {/* R-10 -- LA X SE ENCIMABA CON EL TITULO: la X es absoluta y no ocupa lugar en el flujo, asi que los tres
            titulos, el indicador de cola y el aviso de duplicado le dejan su aire a la derecha (32 px).
            pantallas-alumno (G): la X de la ventana del alumno, con su area de 44. */}
        <XDeLaVentana onClose={onClose} etiqueta={t('dashboard:auto_split.cancel_button')} />
        <div className={CUERPO_DE_LA_VENTANA} data-testid="auto-split-cuerpo">

        {queuePosition && queuePosition.total > 1 && (
          <div className="text-[11px] uppercase tracking-wider text-[#F7A06A] font-semibold mb-2 pr-8" data-testid="auto-split-queue-indicator">
            {t('dashboard:auto_split.queue_indicator', { current: queuePosition.current, total: queuePosition.total })}
          </div>
        )}

        {/* Duplicate-in-pending notice */}
        {dup && (
          <div
            className="flex items-start gap-2 bg-[#1A120C] border border-[#3A2414] rounded-xl px-3 py-2.5 mb-4 mr-8 text-[13px] text-[#C8C8C8]"
            data-testid="auto-split-duplicate-notice"
          >
            <FileStack className="w-3.5 h-3.5 shrink-0 mt-0.5 text-[#F27A2E]" />
            <span className="flex-1">{t('dashboard:auto_split.duplicate_notice')}</span>
          </div>
        )}

        {isResume ? (
          <div className="mb-3">
            <h2 id="auto-split-titulo" className="text-[17px] font-semibold text-[#F4F4F3] leading-snug line-clamp-2 break-words pr-8" data-testid="auto-split-header" title={filename}>
              {t('dashboard:auto_split.resume_title', { filename })}
            </h2>
            <p className="text-[13px] text-[#C8C8C8] mt-1" data-testid="auto-split-resume-subtitle">
              {t('dashboard:auto_split.resume_subtitle', { partsLeft: t('dashboard:auto_split.parts_left', { count: n }), count: nextCost, have: snap.credits_available })}
            </p>
            {porCalidad}
          </div>
        ) : (wall === 'partial' || wall === 'k0') && !isDuplicate && !isGuestMode ? (
          /* B1 outcome-first: the user's RESULT is the headline. The redundant subtitle
             (auto_split.title) was removed per the copy revision — the lead already names
             the parts, and the enriched parts legend carries the "rest goes to Pending" detail. */
          <div className="mb-3">
            <h2 id="auto-split-titulo" className="text-[17px] font-semibold text-[#F4F4F3] leading-snug pr-8" data-testid="auto-split-header">
              {s7
                ? tituloDeLaVentana(t, s7)
                : wall === 'k0'
                ? t('dashboard:auto_split.k0_lead', { n })
                : t('dashboard:auto_split.partial_lead', { k, parts: partsAmt(n) })}
            </h2>
            {/* series-7: el renglon de las demas (U3, U4 o U5 segun lo que falte; desde el
                bloque T, sin_mas_* si nada cabe ni en Pendientes) y, si algo no cupo en
                Pendientes, U6. Es lo UNICO que se agrega a la ventana: va dentro del bloque
                del titulo, con las clases del subtitulo de reanudar. (Con desborde cambian
                ademas los TEXTOS del boton de procesar y de la leyenda: ver s7ConDesborde.) */}
            {s7Resto && (
              <p className="text-[13px] text-[#C8C8C8] mt-1" data-testid="auto-split-s7-resto">{s7Resto}</p>
            )}
            {s7Desborde && (
              <p className="text-[13px] text-[#C8C8C8] mt-1" data-testid="auto-split-s7-desborde">{s7Desborde}</p>
            )}
            {porCalidad}
          </div>
        ) : porCalidad ? (
          /* estilos-1 (Bloque D): con la oracion de las partes el titulo va en su bloque (el margen de abajo pasa al
             bloque, como en las otras dos ramas); sin ella (muro impossible) el titulo es el de antes, byte a byte. */
          <div className="mb-3">
            <h2 id="auto-split-titulo" className="text-[17px] font-semibold text-[#F4F4F3] leading-snug pr-8" data-testid="auto-split-header">
              {s7
                ? tituloDeLaVentana(t, s7)
                : t('dashboard:auto_split.title', { parts: partsAmt(n) })}
            </h2>
            {porCalidad}
          </div>
        ) : (
          <h2 id="auto-split-titulo" className="text-[17px] font-semibold text-[#F4F4F3] mb-3 leading-snug pr-8" data-testid="auto-split-header">
            {/* FIX-2: impossible gets its own title (no "split into N parts" promise). */}
            {s7
              ? tituloDeLaVentana(t, s7)
              : wall === 'impossible'
              ? t('dashboard:auto_split.impossible_title')
              : t('dashboard:auto_split.title', { parts: partsAmt(n) })}
          </h2>
        )}

        {/* ── Three-layer math: NEEDS (what the document requires) / YOU HAVE (chips) /
            MISSING (deficit badge). Each layer is its own row; together they close the
            arithmetic visually (have 3 · needs 11 · missing 8). ── */}

        {/* P4: ONE quiet adaptive math line per limiting coin, glued to the filename. */}
        {(mathLines.length > 0 || !isResume) && (
          <div className="mb-4 space-y-0.5">
            {mathLines.map((line, i) => (
              <p key={i} className="text-[13px] text-[#C8C8C8]" data-testid={`auto-split-math-line-${i}`}>{line}</p>
            ))}
            {!isResume && (
              <p className="text-[13px] text-[#9A9A9A] truncate" title={filename} data-testid="auto-split-filename">{filename}</p>
            )}
          </div>
        )}

        {/* DUP1: status line of the EXISTING group (from the pending summary) */}
        {isDuplicate && duplicateGroup && (
          duplicateGroup.processable ? (
            <p className="text-[13px] text-[#4ADE80] mb-4" data-testid="auto-split-dup-group-status">
              {t('dashboard:auto_split.dup_group_processable', { count: duplicateGroup.next_part_cost })}
            </p>
          ) : (
            <p className="text-[13px] text-[#C8C8C8] mb-4" data-testid="auto-split-dup-group-status">
              {t('dashboard:pending.next_part_line', {
                count: duplicateGroup.next_part_cost,
                have: duplicateGroup.affordability?.snapshot?.credits_available ?? 0,
              })}
            </p>
          )
        )}

        {/* ───────── Screen body ───────── */}

        {/* Guest mode: the hook (what a free account unlocks) + a secondary continuity
            micro-line reassuring the document is kept. */}
        {isGuestMode && (
          <>
            <p className="text-[14px] text-[#F4F4F3] mb-1" data-testid="auto-split-guest-hook">
              {isGuestLarge
                ? t('dashboard:auto_split.guest_hook_large', { parts: partsAmt(n) })
                : k >= n
                  ? t('dashboard:auto_split.guest_hook_all', { parts: partsAmt(n) })
                  : t('dashboard:auto_split.guest_hook', { kParts: partsAmt(k), count: r })}
            </p>
            <p className="text-[13px] text-[#9A9A9A] mb-4" data-testid="auto-split-guest-waiting-note">
              {t('dashboard:auto_split.guest_waiting_note')}
            </p>
          </>
        )}

        {/* Resume only: the "other parts stay in Pending" line. Upload partial/k0 no longer
            render an explanation paragraph — that detail moved into the enriched parts legend
            (parts_legend_*), so pending_explain_* is deprecated (kept in JSON, not rendered). */}
        {!isDuplicate && !isGuestMode && isResume && (wall === 'partial' || wall === 'k0') && (
          <p className="text-[13px] leading-5 text-[#C8C8C8] mb-4" data-testid="auto-split-pending-explain">
            {t('dashboard:auto_split.resume_explain', { count: r })}
          </p>
        )}

        {/* overcap: human lead (what's happening) above the suggestion cards.

            APP-1 B4 (#401) — Y SÓLO CUANDO DE VERDAD NO HAY DOCUMENTO GUARDADO.
            `overcap_lead` abre con «Este documento aún no está guardado». Nació para el
            carril de texto, donde era cierta. Hoy es falsa en cuanto hay placeholder
            detrás —el escaneado siempre lo tuvo, y desde B2 el texto grande también—, y
            el 11-sep le tocaba a un usuario real. `esEscaneado` es exactamente «hay
            documento guardado»: es `!!preview.documento_base_id`.
            NO se crea ni se cambia ninguna llave: sólo se condiciona cuándo se pinta. La
            segunda mitad de la frase (que falta espacio en Pendiente) no se pierde — la
            línea de matemáticas de arriba la dice con los números exactos, y ésa se
            sigue pintando en los dos casos. */}
        {!isDuplicate && !isGuestMode && !esEscaneado && wall === 'overcap' && (
          <p className="text-[14px] text-[#F4F4F3] mb-3" data-testid="auto-split-overcap-lead">
            {t('dashboard:auto_split.overcap_lead', { n })}
          </p>
        )}

        {/* contact / impossible messages */}
        {!isDuplicate && !isGuestMode && wall === 'contact' && (
          <p className="text-[14px] text-[#C8C8C8] bg-[#101010] border border-[#262626] rounded-xl px-3 py-2.5 mb-4 leading-relaxed" data-testid="auto-split-pro-max">
            {t('dashboard:auto_split.pro_max_msg')}
          </p>
        )}
        {!isDuplicate && !isGuestMode && wall === 'impossible' && (
          <>
            <p className="text-[14px] text-[#C8C8C8] bg-[#101010] border border-[#262626] rounded-xl px-3 py-2.5 mb-3 leading-relaxed" data-testid="auto-split-impossible-msg">
              {t('dashboard:auto_split.impossible_msg')}
            </p>
            {/* Manual-split tip — informational box, NOT a button */}
            <div className="flex items-start gap-2 bg-[#101010] border border-[#262626] rounded-xl px-3 py-2.5 mb-4 text-[13px] text-[#9A9A9A]" data-testid="auto-split-manual-tip">
              <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-[#F27A2E]" />
              <span>{t('dashboard:auto_split.manual_split_tip')}</span>
            </div>
          </>
        )}

        {/* Visual parts map + legend — directly above the primary button */}
        {!isDuplicate && showPartsMap && partsSegments.length > 0 && (
          <div className="mb-3" data-testid="auto-split-parts-map">
            <PartsMap segments={partsSegments} t={t} testid="auto-split-parts-map-bar" estilo="alumno" />
            {s7Leyenda && (
              <ColoresDeLaBarra tramos={nombresDeLosTramos(s7, k)} t={t} testid="auto-split-parts-colores" estilo="alumno" />
            )}
            <p className="text-[13px] text-[#C8C8C8] mt-2" data-testid={s7Leyenda ? 'auto-split-parts-leyenda' : undefined}>{partsLegend}</p>
          </div>
        )}

        </div>
        {/* pantallas-alumno (G): EL PIE, fijo abajo (los botones y el descartar o cancelar). */}
        <div className={PIE_DE_LA_VENTANA} data-testid="auto-split-pie">
        {/* ───────── Action buttons ───────── */}
        <div className="space-y-2">
          {/* Guest mode: ONE action — create the account that unlocks processing. */}
          {isGuestMode && (
            <PrimaryBtn
              disabled={confirmLoading}
              testid="auto-split-guest-signup-btn"
              label={t('dashboard:auto_split.guest_cta')}
              onClick={() => { emit('guest_signup'); onGuestSignup && onGuestSignup(); }}
            />
          )}

          {/* X1 duplicate variant: the one obvious action is to continue in the existing group */}
          {!isGuestMode && isDuplicate && (
            <PrimaryBtn
              disabled={confirmLoading}
              testid="auto-split-view-duplicate-btn"
              label={t('dashboard:auto_split.duplicate_link')}
              onClick={() => { emit('view_duplicate'); onGoPending && onGoPending(dup.parent_doc_id); }}
            />
          )}

          {!isDuplicate && !isGuestMode && wall === 'full' && (
            <PrimaryBtn
              disabled={confirmLoading}
              testid="auto-split-process-all-btn"
              /* L3.6 (censo #32): en modo `resume` este botón sale con `n` = las partes que
                 FALTAN, así que con una sola pendiente decía "Procesar las 1 parte". El
                 gemelo de al lado (wall `partial`) ya tenía su singular propio desde
                 `btn_resume_single`; éste no. Misma medicina. */
              label={n === 1
                ? t('dashboard:auto_split.btn_process_all_single')
                : t('dashboard:auto_split.btn_process_all', { parts: partsAmt(n) })}
              onClick={() => { emit('process_all'); onProcess && onProcess(undefined); }}
            />
          )}

          {!isDuplicate && !isGuestMode && wall === 'partial' && (
            <PrimaryBtn
              disabled={confirmLoading}
              testid="auto-split-process-partial-btn"
              label={isResume && k === 1
                ? t('dashboard:auto_split.btn_resume_single', { count: nextCost })
                : t(isResume || s7ConDesborde ? 'dashboard:auto_split.btn_resume_partial' : 'dashboard:auto_split.btn_process_partial', { k })}
              onClick={() => { emit('process_partial'); onProcess && onProcess(k); }}
            />
          )}

          {/* #369: en el escaneado NO hay «Guardar en Pendiente de procesar». El documento
              ya está guardado —es un `por_confirmar` con su tarjeta— y este botón lo
              convertiría en partes pendientes sin que el alumno gane nada: seguiría sin
              poder estudiarlo, y perdería el estado que sí sabe volver a preguntarle.
              El camino de texto lo conserva: ahí el documento aún no existe y guardar es
              justamente lo que evita perderlo. */}
          {!isDuplicate && !isGuestMode && !esEscaneado && wall === 'k0' && !s7NadaQueGuardar && (
            <PrimaryBtn
              disabled={confirmLoading}
              testid="auto-split-save-pending-btn"
              label={t('dashboard:auto_split.btn_save_pending')}
              onClick={() => { emit('save_pending'); onSavePending && onSavePending(); }}
            />
          )}

          {/* FIX-1: contact no longer offers "contact support". The top plan is maxed and freeing
              Pending space IS the fix, so the free-space button below is the contact primary.
              Only impossible keeps the contact-custom primary (no plan/space can save it). */}
          {!isDuplicate && !isGuestMode && wall === 'impossible' && (
            <PrimaryBtn
              disabled={confirmLoading}
              testid="auto-split-contact-btn"
              label={t('dashboard:auto_split.btn_contact_support')}
              onClick={() => { emit('contact_support'); onContactSupport && onContactSupport(); }}
            />
          )}

          {/* Suggestion cards - AFTER the primary (X2) */}
          {!isDuplicate && !isGuestMode && (wall === 'partial' || wall === 'k0' || wall === 'overcap') && (
            <AutoSplitSuggestionCards t={t} suggestions={aff.suggestions} n={n} onSelect={onCardClick}
              series7={s7 || undefined} idioma={idioma} estilo="alumno" />
          )}

          {/* Secondary: buy credits — RESUME only (upload partial/k0 are card-driven, P5) */}
          {isResume && showBuyCredits && (
            <SecondaryBtn
              testid="auto-split-buy-credits-btn"
              label={t('dashboard:auto_split.buy_pack_button')}
              onClick={() => { emit('buy_pack'); onBuyPack && onBuyPack(); }}
            />
          )}

          {/* Free space in Pending (overcap / contact). NOT in impossible: freeing pending
              storage can never make a does-not-fit-on-any-plan document fit — R2. Also gated on
              free_space_resolves: freeing only helps when the remainder (n-k) fits THIS plan's
              pending tope. On a low plan whose tope is smaller than the remainder (e.g. free, 12
              parts to save > tope 10) freeing is a dead end, so the button is hidden. `!== false`
              keeps the button for any payload predating the backend flag (no contact regression).
              In contact the flag is always true ((n-k) <= 20, Pro's pending tope). */}
          {!isDuplicate && !isGuestMode && (wall === 'overcap' || wall === 'contact')
            && aff.free_space_resolves !== false && (
            /* FIX-1: in contact this IS the action (primary, orange); in overcap it stays a
               secondary nudge alongside the upgrade cards. */
            wall === 'contact' ? (
              <PrimaryBtn
                disabled={confirmLoading}
                testid="auto-split-free-space-pending-btn"
                label={t('dashboard:auto_split.btn_free_space_pending')}
                onClick={() => { emit('free_space'); onGoPending && onGoPending(); }}
              />
            ) : (
              <SecondaryBtn
                testid="auto-split-free-space-pending-btn"
                label={t('dashboard:auto_split.btn_free_space_pending')}
                onClick={() => { emit('free_space'); onGoPending && onGoPending(); }}
              />
            )
          )}

        </div>

        {/* Footer: upload k0/partial = discreet "Discard" (close WITHOUT saving, the X saves);
            everything else = a real Cancel/close.

            #368 — EN EL ESCANEADO EL PIE ES SIEMPRE «DESCARTAR», EN TODOS LOS MUROS.
            Hasta hoy el pie lo decidía sólo el muro, así que un escaneado en `full`,
            `overcap`, `contact`, `impossible` o en modo invitado enseñaba «Cancelar» —
            y «Cancelar» borraba el documento. Un botón que dice «cancelar» y borra el
            trabajo del alumno es la trampa que esta firma cierra: aquí el borrado se
            llama por su nombre y pide confirmación en dos pasos. La X sigue existiendo
            y ahora hace lo que promete (cerrar), así que ninguna salida es destructiva
            por accidente. */}
        {/* #372: el muro `full` de un archivo de TEXTO gana el mismo enlace «Descartar este
            documento» del escaneado (misma llave, mismo nodo, mismo estilo, misma posición y
            misma confirmación de dos pasos). «Cancelar» sigue existiendo debajo y hace lo
            mismo de hoy; los demás muros no cambian. */}
        {isDuplicate ? null : (esEscaneado || (!isResume && !isGuestMode && (wall === 'partial' || wall === 'k0' || wall === 'full'))) ? (
          discardConfirm ? (
            <div className="mt-3" data-testid="auto-split-discard-confirm">
              <p className="text-[13px] text-[#C8C8C8] mb-2 text-center">{t('dashboard:auto_split.discard_confirm')}</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setDiscardConfirm(false)}
                  className={`flex-1 h-11 rounded-xl bg-[#1B1B1B] border border-[#2C2C2C] text-[#F4F4F3] text-[14px] font-semibold ${APRETADO}`}
                  data-testid="auto-split-discard-keep"
                >
                  {t('dashboard:auto_split.discard_keep')}
                </button>
                <button
                  onClick={() => { emit('discard'); onDiscard && onDiscard(); }}
                  className={`flex-1 h-11 rounded-xl bg-[#DC2626] text-white text-[14px] font-semibold ${APRETADO}`}
                  data-testid="auto-split-discard-yes"
                >
                  {t('dashboard:auto_split.discard_yes')}
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setDiscardConfirm(true)}
              className="w-full mt-2 text-[13px] text-[#9A9A9A] hoverable:text-[#F87171] transition-colors py-2 min-h-[44px]"
              data-testid="auto-split-discard-link"
            >
              {t('dashboard:auto_split.discard_link')}
            </button>
          )
        ) : null}
        {!isDuplicate && !(esEscaneado || (!isResume && !isGuestMode && (wall === 'partial' || wall === 'k0'))) && (
          <button
            onClick={() => onClose && onClose()}
            className={`${BOTON_DE_LA_VENTANA} mt-2 bg-[#1B1B1B] border border-[#2C2C2C] text-[#F4F4F3] ${APRETADO}`}
            data-testid="auto-split-cancel-btn"
          >
            {t('dashboard:auto_split.cancel_button')}
          </button>
        )}

        </div>
      </div>
    </div>
  );
}
